/**
 * History: the asset's events, newest first, paged on the server. Each row says who, when and what; its changes
 * open below it, text as a Before and After word diff (`DiffPanes`) and other fields as one line. Internal events
 * reach only readers with `asset.view.internal` (the server leaves them out for everyone else).
 */

import { useState } from 'react';
import { History } from 'lucide-react';

import { useAssetHistory } from '@/api/queries';
import type { HistoryEvent } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { DiffPanes } from '@/ui/DiffPanes';
import { Pagination } from '@/ui/Pagination';
import { LoadFailure, LoadingCard, StateCard } from '@/ui/StateCard';
import { formatDateTime } from '@/ui/format';

import { actionText, changeList } from './historyText';

function Changes({ event }: { event: HistoryEvent }) {
  const changes = changeList(event);
  if (changes.length === 0) return null;
  return (
    <details className="history-changes">
      <summary>Show what changed</summary>
      {changes.map((c) =>
        c.kind === 'text' ? (
          <div key={c.field} className="history-diff">
            <h4>{c.field}</h4>
            <DiffPanes current={c.before} proposed={c.after} titles={['Before', 'After']} />
          </div>
        ) : (
          <p key={c.field}><b>{c.field}:</b> {c.text}</p>
        ),
      )}
    </details>
  );
}

function EventRow({ event }: { event: HistoryEvent }) {
  return (
    <li className="history-event">
      <div className="history-event-head">
        <b>{actionText(event)}</b>
        {event.audience === 'internal' && <Badge variant="audience" value="internal" />}
        <span className="hint">
          {event.actor?.display_name ?? 'System'}, {formatDateTime(event.at)}
        </span>
      </div>
      {event.case_id && <p className="hint">The details are on the success case.</p>}
      <Changes event={event} />
    </li>
  );
}

export function HistoryTab({ assetId }: { assetId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const history = useAssetHistory(assetId, page, pageSize);

  if (history.isPending) return <LoadingCard label="Loading the history" />;
  if (history.error) return <LoadFailure error={history.error} onRetry={() => void history.refetch()} />;
  if (history.data.total === 0) {
    return <StateCard icon={<History aria-hidden="true" />} title="No history yet" />;
  }
  return (
    <div className="asset-history">
      <ol className="history-list" aria-label="History">
        {history.data.events.map((e) => <EventRow key={e.id} event={e} />)}
      </ol>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={history.data.total}
        noun={history.data.total === 1 ? 'event' : 'events'}
        onPage={setPage}
        onPageSize={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}
